import * as Sentry from '@sentry/react';
import {
  Account_Update,
  AccountType,
  Contact_Update,
  getV1CompaniesId,
  ModelError_Response,
  patchV1CompaniesCompanyIdAccountsId,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { isNil } from 'lodash-es';

import { Account } from '~hooks/useAccount/models';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { Company } from '~hooks/useCompany/models';
import { formatPhoneForSubmit } from '~utils/utilFunctions';

import { ConnectedAccountContactPanelHandler } from '../ConnectedAccountContactPanel/ConnectedAccountContactPanel';
import { NotesPanelHandler } from '../NotesPanel/NotesPanel';
import { TruckCapacityPanelHandler } from '../TruckCapacityPanel/TruckCapacityPanel';
import { orderCommittedTruckCapacityRows } from '../TruckCapacityPanel/truckCapacitySchema';
import { AccountConfigurationPanelHandler } from './AccountConfigurationPanel/AccountConfigurationPanel';
import { AccountDetailsPanelHandler } from './AccountDetailsPanel/AccountDetailsPanel';

export async function collectDetailsToUpdateAccount(
  ref: React.RefObject<AccountDetailsPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  let address = AddressItem.deparseCreate(data.address as AddressItem);
  const hasEmptyAddress = !address?.thoroughfare;

  if (hasEmptyAddress) {
    const response = await getV1CompaniesId({ path: { id: data.company.value } });
    const parsed = Company.parse(response.data.data);
    address = AddressItem.deparseCreate(parsed.address);
  }

  if (!address) {
    const error = new Error('[collectDetailsToUpdateAccount] missing address');
    Sentry.captureException(error);
    throw error;
  }

  const values: Pick<
    Account_Update,
    | 'name'
    | 'department_id'
    | 'address'
    | 'billing_address'
    | 'external_id'
    | 'accounting_id'
    | 'dot_number'
    | 'label_ids'
  > = {
    name: data.name,
    department_id: data.department?.value || null,
    address,
    billing_address: data.isBillingAddressSame
      ? address
      : AddressItem.deparseCreate(data.billingAddress as AddressItem),
    external_id: data.externalId || null,
    accounting_id: data.accountingId || null,
    dot_number: data.dotNumber || null,
    label_ids: data.labels?.map((label) => label.id),
  };

  return { values, companyId: data.company.value };
}

export async function collectContactsToUpdateAccount(
  ref: React.RefObject<ConnectedAccountContactPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const primaryContact: Contact_Update = {
    name: `${data.primaryContact.firstName} ${data.primaryContact.lastName}`,
    email: data.primaryContact.email || undefined,
    phone: formatPhoneForSubmit(data.primaryContact.phone),
  };
  const billingContact: Contact_Update = {
    name:
      data.billingContact?.firstName && data.billingContact.lastName
        ? `${data.billingContact.firstName} ${data.billingContact.lastName}`
        : undefined,
    email: data.billingContact?.email || undefined,
    phone: formatPhoneForSubmit(data.billingContact?.phone),
  };

  const values: Pick<Account_Update, 'primary_contact' | 'billing_contact'> = {
    primary_contact: primaryContact,
    billing_contact: data.isBillingContactSame ? primaryContact : billingContact,
  };

  return values;
}

export async function collectConfigurationToUpdateAccount(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Update, 'account_types'> & {
    jobs_splittable?: boolean;
    equipment_options_restricted?: boolean;
  } = {
    account_types: data.accountTypes as AccountType[],
    jobs_splittable: data.jobsSplittable ?? false,
    equipment_options_restricted: data.equipmentOptionsRestricted ?? false,
  };

  return values;
}

export async function collectNotesToUpdateAccount(
  ref: React.RefObject<NotesPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Update, 'notes'> = {
    notes: data.notes?.trim() || null,
  };

  return values;
}

export async function collectTruckCapacityToUpdateAccount(
  ref: React.RefObject<TruckCapacityPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const hasRows = data.committedTruckCapacities.length > 0;
  const truckCount =
    hasRows || data.truckCount === '' || isNil(data.truckCount)
      ? undefined
      : Number(data.truckCount);
  const committedTruckCapacitiesAttributes = orderCommittedTruckCapacityRows(data).map(
    (rowIndex, payloadIndex) => {
      if (rowIndex === undefined) {
        return {
          id: data.removedCommittedTruckCapacityIds[payloadIndex],
          _destroy: true,
        };
      }

      const row = data.committedTruckCapacities[rowIndex];

      return {
        ...(row.id ? { id: row.id } : {}),
        equipment_type_id: row.equipmentType.value,
        truck_count: Number(row.count),
      };
    },
  );

  const values: Pick<Account_Update, 'connected_account'> = {
    connected_account: {
      truck_count: truckCount,
      ...(committedTruckCapacitiesAttributes.length > 0 && {
        committed_truck_capacities_attributes: committedTruckCapacitiesAttributes,
      }),
    },
  };

  return values;
}

export async function updateAccount(
  data: Account_Update,
  accountId: string,
  companyId: string,
) {
  try {
    const response = await patchV1CompaniesCompanyIdAccountsId({
      path: { 'company-id': companyId, id: accountId },
      body: data,
    });

    return { account: Account.parse(response.data.data) };
  } catch (error) {
    const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

    if (isUnprocessableEntityError) {
      const typedError = error as AxiosError<ModelError_Response>;

      return { errors: typedError.response?.data.error.errors ?? [] };
    }

    throw error;
  }
}
